"use client";

import type { ComponentProps } from "react";
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { classed, clsx } from "@frend-digital/ui";
import { useFormContext } from "react-hook-form";
import styles from "../form.module.css";

interface InputProps extends React.ComponentProps<"input"> {
  label?: string;
  icon?: React.ReactNode;
  error?: string | boolean;
  success?: string | boolean;
  handleSubmit?: () => void;
  submitField?: boolean;
}
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      id: initialId,
      className,
      error,
      handleSubmit,
      onChange,
      submitField,
      success,
      defaultValue,
      required,
      children,
      ...props
    },
    externalRef
  ) => {
    const [hasValue, setHasValue] = useState(!!defaultValue);
    const internalId = useId();
    const id = initialId ?? internalId;

    const ref = useRef<HTMLInputElement>(null!);
    useImperativeHandle(externalRef, () => ref.current);

    // Check if input has value on mount and when it changes
    useEffect(() => {
      const input = ref.current;
      if (!input) return;

      // Initial check
      setHasValue(!!input.value);

      const interval = setInterval(() => {
        if (document.hidden) return;

        const hasInputValue = !!input.value;
        setHasValue(hasInputValue);

        // Stop polling once we detect a value (autofill happened)
        if (hasInputValue) {
          clearInterval(interval);
        }
      }, 250);

      return () => {
        clearInterval(interval);
      };
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setHasValue(!!value);

      if (onChange) {
        onChange(e);
      }
    };

    const handleWrapperClick = () => {
      if (props.type === "date" && ref.current) {
        ref.current.focus();
        // Try to open the native date picker
        if ("showPicker" in ref.current) {
          try {
            (ref.current as any).showPicker();
          } catch (e) {
            // showPicker might not be supported in all browsers
          }
        }
      }
    };

    return (
      <div
        className={clsx(styles.form__field, className)}
        onClick={handleWrapperClick}
        style={props.type === "date" ? { cursor: "pointer" } : undefined}
      >
        {label && (
          <label
            htmlFor={id}
            className={clsx(
              styles.Label,
              (!!defaultValue || hasValue) && styles.active,
              required && styles.required
            )}
          >
            {label}
          </label>
        )}

        <input
          {...props}
          ref={ref}
          id={id}
          onChange={handleChange}
          defaultValue={defaultValue}
          className={clsx(
            styles.input,
            (!!defaultValue || hasValue) && styles.active
          )}
        />

        {error && (
          <span className={styles.error} data-error>
            {error}
          </span>
        )}

        {hasValue && submitField && (
          <InputAction className={styles.submitButton} onClick={handleSubmit}>
            <p>SUBMIT</p>
          </InputAction>
        )}

        {success && typeof success === "string" && (
          <span className={clsx(styles.statusText, styles.successText)}>
            {success}
          </span>
        )}

        {children}
      </div>
    );
  }
);

Input.displayName = "Input";

export const InputAction = ({
  children,
  className,
  ...props
}: React.ComponentProps<"button">) => {
  return (
    <button
      type="submit"
      className={clsx(styles.inputAction, className)}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * React Hook Form powered InputField
 */
export const InputField = ({
  name,
  submitField,
  ...props
}: ComponentProps<typeof Input> & { name: string }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  return (
    <Input
      {...register(name)}
      error={errors[name]?.message as string}
      {...props}
    />
  );
};

export const Select = classed("select", styles.root);
